import { CALLSENSE_SCORE_FIELDS, type CallsenseCallRow } from '@utazzo/shared';
import React, { useMemo, useState } from 'react';

import { CallDetailDrawer } from '../components/CallDetailDrawer';
import { CallingReportTable } from '../components/CallingReportTable';
import {
  formatCallDate,
  OUTCOME_COLORS,
  OUTCOME_LABELS,
  SCORE_LABELS,
} from '../components/callsense-format';
import { CALLSENSE_FONT_HREF, CR_TOKENS_CSS } from '../components/callsense-tokens';
import {
  useCallsenseCalls,
  useCallsenseCallingReport,
  useCallsenseOverview,
} from '../use-callsense';

import { useCurrentUser } from '@/features/auth/use-auth';

const isoDay = (d: Date): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(d);
const daysAgo = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDay(d);
};

type Channel = 'all' | 'cd' | 'wa';
type Range = 'today' | '7d' | '30d' | '90d' | 'month';
type Mode = 'today' | 'total';
const RANGE_DAYS: Record<Range, number> = { today: 0, '7d': 7, '30d': 30, '90d': 90, month: 30 };
const RANGE_LABEL: Record<Range, string> = {
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  month: 'This month',
};

/** Segmented toggle matching the design. */
function Seg<T extends string>({
  value,
  opts,
  onChange,
}: {
  value: T;
  opts: { v: T; label: string; soon?: boolean }[];
  onChange: (v: T) => void;
}): React.JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        gap: 2,
        background: 'var(--cr-sub2)',
        borderRadius: 10,
        padding: 3,
      }}
    >
      {opts.map((o) => {
        const on = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            style={{
              padding: '6px 12px',
              fontSize: 14,
              fontWeight: 700,
              border: 0,
              borderRadius: 8,
              cursor: 'pointer',
              background: on ? 'var(--cr-card)' : 'transparent',
              color: on ? 'var(--cr-tx)' : 'var(--cr-mut)',
              boxShadow: on ? '0 1px 3px rgba(0,0,0,.12)' : 'none',
            }}
          >
            {o.label}
            {o.soon && (
              <span style={{ fontSize: 11, color: 'var(--cr-warn)', marginLeft: 3 }}>soon</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

const SectionHead = ({
  label,
  right,
}: {
  label: string;
  right?: React.ReactNode;
}): React.JSX.Element => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <div
      style={{
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '.12em',
        textTransform: 'uppercase',
        color: 'var(--cr-mut)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </div>
    <div style={{ flex: 1, height: 1, background: 'var(--cr-bd)' }} />
    {right}
  </div>
);

const Card: React.CSSProperties = {
  background: 'var(--cr-card)',
  border: '1px solid var(--cr-bd)',
  borderRadius: 14,
  boxShadow: 'var(--cr-sh)',
};

type Kpi = {
  label: string;
  value: string;
  color: string;
  sub: string;
  cd: string;
  wa: string;
  spark: string;
  tip?: string;
  /** % change vs the comparison period; undefined = no delta pill. */
  delta?: number;
  /** Whether an increase is "good" (green). Defaults true. */
  goodUp?: boolean;
};

const KpiCard = ({ k }: { k: Kpi }): React.JSX.Element => (
  <div
    title={k.tip}
    style={{
      ...Card,
      display: 'flex',
      flexDirection: 'column',
      gap: 7,
      padding: '13px 14px',
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontSize: 13,
        fontWeight: 700,
        color: 'var(--cr-mut)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {k.label}
    </div>
    <div
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}
    >
      <div
        style={{
          fontSize: 26,
          fontWeight: 800,
          letterSpacing: '-.03em',
          lineHeight: 1,
          color: k.color,
          whiteSpace: 'nowrap',
        }}
      >
        {k.value}
      </div>
      {k.delta != null ? (
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            color:
              k.delta === 0
                ? 'var(--cr-mut)'
                : k.delta >= 0 === (k.goodUp ?? true)
                  ? 'var(--cr-ok)'
                  : 'var(--cr-bad)',
          }}
        >
          {k.delta >= 0 ? '+' : ''}
          {k.delta}%
        </span>
      ) : null}
    </div>
    <svg
      viewBox="0 0 100 24"
      preserveAspectRatio="none"
      style={{ width: '100%', height: 22, display: 'block' }}
    >
      <polyline
        points={k.spark}
        fill="none"
        stroke={k.color}
        strokeWidth={2}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
    <div
      style={{
        fontSize: 12.5,
        color: 'var(--cr-mut)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {k.sub}
    </div>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 12.5,
        color: 'var(--cr-mut)',
        whiteSpace: 'nowrap',
        borderTop: '1px solid var(--cr-bd2)',
        paddingTop: 6,
      }}
    >
      <span>
        CD <b style={{ color: 'var(--cr-tx)' }}>{k.cd}</b>
      </span>
      <span>
        WA <b style={{ color: 'var(--cr-wa)' }}>{k.wa}</b>
      </span>
    </div>
  </div>
);

/** Call outcome: a segmented share bar + a ranked list (dot, label, count, %). */
const OutcomeCard = ({
  arcs,
  connected,
}: {
  arcs: { label: string; n: number; color: string }[];
  connected: number;
}): React.JSX.Element => {
  const total = arcs.reduce((s, a) => s + a.n, 0) || 0;
  return (
    <div
      style={{
        ...Card,
        gridColumn: 'span 3',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '14px 16px',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontSize: 15, fontWeight: 800 }}>Call outcome</div>
        <span style={{ fontSize: 13, color: 'var(--cr-mut)' }}>
          <b style={{ color: 'var(--cr-tx)' }}>{connected}</b> connected
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          height: 8,
          borderRadius: 999,
          overflow: 'hidden',
          background: 'var(--cr-sub2)',
        }}
      >
        {arcs.map((a, i) => {
          const w = total ? (a.n / total) * 100 : 0;
          return w > 0 ? <div key={i} style={{ width: `${w}%`, background: a.color }} /> : null;
        })}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {arcs.map((a, i) => {
          const pct = total ? Math.round((a.n / total) * 100) : 0;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 999,
                  background: a.color,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  color: 'var(--cr-tx)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {a.label}
              </span>
              <b style={{ color: 'var(--cr-tx)' }}>{a.n}</b>
              <span style={{ color: 'var(--cr-mut)', width: 36, textAlign: 'right' }}>{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Customer sentiment: big positive %, a segmented bar, and three tiles. */
const SentimentCard = ({
  positive,
  neutral,
  negative,
}: {
  positive: number;
  neutral: number;
  negative: number;
}): React.JSX.Element => {
  const total = positive + neutral + negative || 0;
  const pct = (n: number): number => (total ? Math.round((n / total) * 100) : 0);
  const tiles: { label: string; n: number; color: string }[] = [
    { label: 'Positive', n: positive, color: 'var(--cr-ok)' },
    { label: 'Neutral', n: neutral, color: 'var(--cr-mut)' },
    { label: 'Negative', n: negative, color: 'var(--cr-bad)' },
  ];
  return (
    <div
      style={{
        ...Card,
        gridColumn: 'span 3',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '14px 16px',
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 6,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 800 }}>Customer sentiment</div>
        <span style={{ fontSize: 12, color: 'var(--cr-mut)', whiteSpace: 'nowrap' }}>
          AI-rated · connected calls
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 30, fontWeight: 800, color: 'var(--cr-ok)', lineHeight: 1 }}>
          {pct(positive)}%
        </span>
        <span style={{ fontSize: 14, color: 'var(--cr-mut)' }}>positive</span>
      </div>
      <div
        style={{
          display: 'flex',
          height: 8,
          borderRadius: 999,
          overflow: 'hidden',
          background: 'var(--cr-sub2)',
        }}
      >
        {tiles.map((t, i) => {
          const w = total ? (t.n / total) * 100 : 0;
          return w > 0 ? <div key={i} style={{ width: `${w}%`, background: t.color }} /> : null;
        })}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
        {tiles.map((t, i) => (
          <div
            key={i}
            style={{
              background: 'var(--cr-sub)',
              borderRadius: 10,
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--cr-mut)',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: 999, background: t.color }} />
              {t.label}
            </span>
            <span style={{ fontSize: 16, fontWeight: 800 }}>
              {t.n}{' '}
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--cr-mut)' }}>
                {pct(t.n)}%
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CallSensePage = (): React.JSX.Element => {
  // Load Manrope (the design's typeface) once, same as the Website Dashboard.
  React.useEffect(() => {
    if (document.querySelector('link[data-cr-font]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = CALLSENSE_FONT_HREF;
    link.setAttribute('data-cr-font', 'true');
    document.head.appendChild(link);
  }, []);

  const [channel, setChannel] = useState<Channel>('all');
  const [range, setRange] = useState<Range>('today');
  const [view, setView] = useState<'admin' | 'tl' | 'agent'>('admin');
  const [mode, setMode] = useState<Mode>('today');
  const [q, setQ] = useState('');
  const [openCallId, setOpenCallId] = useState<string | null>(null);
  const [logF, setLogF] = useState<Record<string, string>>({});

  const from = range === 'today' ? isoDay(new Date()) : daysAgo(RANGE_DAYS[range]);
  const to = isoDay(new Date());
  const reportFrom = mode === 'today' ? isoDay(new Date()) : from;

  const [drillAgent, setDrillAgent] = useState<string | null>(null);
  const me = useCurrentUser();

  const overview = useCallsenseOverview({ from, to, channel });
  const callingReport = useCallsenseCallingReport({ from: reportFrom, to, channel });
  const calls = useCallsenseCalls({ from, to, page: 1, pageSize: 50 });

  const data = overview.data;
  const total = callingReport.data?.rows.find((r) => r.kind === 'total') ?? null;

  // View (Admin/TL/Agent) scopes the report to the current user's team/self;
  // Find-agent search then filters within that scope. Both drive the call log.
  const query = q.trim().toLowerCase();
  const myName = me?.fullName?.trim().toLowerCase() ?? '';
  const reportRows = useMemo(() => {
    const all = callingReport.data?.rows ?? [];
    let scoped = all;
    if (view === 'tl' && myName) {
      const myTl =
        all.find((r) => r.kind === 'tl' && r.name.toLowerCase() === myName) ??
        all.find((r) => r.kind === 'agent' && r.name.toLowerCase() === myName && r.parentId);
      const tlId = myTl?.kind === 'tl' ? myTl.id : (myTl?.parentId ?? null);
      if (tlId) {
        scoped = all.filter(
          (r) =>
            r.kind === 'total' ||
            (r.kind === 'tl' && r.id === tlId) ||
            (r.kind === 'agent' && r.parentId === tlId),
        );
      }
    } else if (view === 'agent' && myName) {
      scoped = all.filter(
        (r) => r.kind === 'total' || (r.kind === 'agent' && r.name.toLowerCase() === myName),
      );
    }
    if (!query) return scoped;
    const keepTl = new Set<string>();
    for (const r of scoped) {
      if (r.kind === 'agent' && r.name.toLowerCase().includes(query) && r.parentId)
        keepTl.add(r.parentId);
      if (r.kind === 'tl' && r.name.toLowerCase().includes(query)) keepTl.add(r.id);
    }
    return scoped.filter((r) => {
      if (r.kind === 'total') return true;
      if (r.kind === 'tl') return keepTl.has(r.id);
      return r.name.toLowerCase().includes(query);
    });
  }, [callingReport.data?.rows, query, view, myName]);
  const connected = data?.totals.calls ?? 0;

  // --- KPI strip (real where sourced; CD-only until WhatsApp analysis lands) ---
  const trend = data?.trend ?? [];
  // Sparklines use the trailing 14-day series so they always show a trend shape.
  const sparkPts = data?.sparkTrend ?? [];
  const spark = (pick: (i: number) => number): string => {
    const pts = sparkPts.length ? sparkPts.map((_, i) => pick(i)) : [0, 0];
    const mx = Math.max(...pts, 1);
    const mn = Math.min(...pts);
    const span = Math.max(1e-6, mx - mn);
    return pts
      .map((v, i) => `${(i / Math.max(1, pts.length - 1)) * 100},${22 - ((v - mn) / span) * 20}`)
      .join(' ');
  };
  const outTotal = total ? total.outbound.total : 0;
  const inTotal = total ? total.inbound.total : 0;
  const attempts = outTotal + inTotal;
  const missed = total ? total.outbound.missed + total.inbound.missed : 0;
  const cdH = total ? (total.callerdeskMinutes / 60).toFixed(1) : '0';
  const waH = total ? (total.whatsappMinutes / 60).toFixed(1) : '0';
  const score5 =
    data?.totals.avgOverallScore != null ? (data.totals.avgOverallScore / 20).toFixed(2) : '-';
  const kpis: Kpi[] = [
    {
      label: 'Total calls',
      value: String(attempts),
      color: 'var(--cr-tx)',
      sub: `${attempts ? Math.round((connected / attempts) * 100) : 0}% connect rate · ${connected} connected`,
      cd: String(connected),
      wa: String(Math.max(0, attempts - connected)),
      spark: spark((i) => sparkPts[i]?.calls ?? 0),
      delta: data?.deltas?.calls,
      goodUp: true,
    },
    {
      label: 'Talk time',
      value: `${total ? ((total.callerdeskMinutes + total.whatsappMinutes) / 60).toFixed(1) : '0'} h`,
      color: 'var(--cr-tx)',
      sub: 'CallerDesk + WhatsApp',
      cd: `${cdH} h`,
      wa: `${waH} h`,
      spark: spark((i) => sparkPts[i]?.calls ?? 0),
      delta: data?.deltas?.minutes,
      goodUp: true,
    },
    {
      label: 'Avg quality score',
      value: score5,
      color: 'var(--cr-acc)',
      sub: 'across 11 skills · 0-5',
      cd: score5,
      wa: 'soon',
      spark: spark((i) => sparkPts[i]?.avgOverallScore ?? 0),
      tip: 'WhatsApp scores arrive with the WhatsApp analysis pipeline',
      delta: data?.deltas?.avgScore,
      goodUp: true,
    },
    {
      label: 'Missed-call rate',
      value: `${attempts ? Math.round((missed / attempts) * 100) : 0}%`,
      color: 'var(--cr-warn)',
      sub: `${missed} missed of ${attempts} attempts`,
      cd: '-*',
      wa: `${total ? total.outbound.missed + total.inbound.missed : 0}`,
      spark: spark((i) => sparkPts[i]?.calls ?? 0),
      tip: '* CallerDesk missed pending the premium webhook',
    },
    {
      label: 'Outbound vs inbound',
      value: `${outTotal} / ${inTotal}`,
      color: 'var(--cr-tx)',
      sub: `${attempts ? Math.round((outTotal / attempts) * 100) : 0}% outbound`,
      cd: `${cdH}h`,
      wa: `${waH}h`,
      spark: spark((i) => sparkPts[i]?.calls ?? 0),
    },
    {
      label: 'Bookings from calls',
      value: String(data?.totals.bookings ?? 0),
      color: 'var(--cr-ok)',
      sub: `${connected ? (((data?.totals.bookings ?? 0) / connected) * 100).toFixed(1) : 0}% of connected`,
      cd: String(data?.totals.bookings ?? 0),
      wa: '0',
      spark: spark((i) => sparkPts[i]?.calls ?? 0),
      delta: data?.deltas?.bookings,
      goodUp: true,
    },
    {
      label: 'Negative sentiment',
      value: String(data?.sentiments?.negative ?? 0),
      color: 'var(--cr-bad)',
      sub: `${connected ? Math.round(((data?.sentiments?.negative ?? 0) / connected) * 100) : 0}% of connected`,
      cd: String(data?.sentiments?.negative ?? 0),
      wa: '0',
      spark: spark((i) => sparkPts[i]?.calls ?? 0),
      delta: data?.deltas?.negative,
      goodUp: false,
    },
    {
      label: 'Escalations',
      value: String(data?.totals.escalations ?? 0),
      color: 'var(--cr-bad)',
      sub: 'flagged by AI for a manager callback',
      cd: String(data?.totals.escalations ?? 0),
      wa: '0',
      spark: spark((i) => sparkPts[i]?.calls ?? 0),
      delta: data?.deltas?.escalations,
      goodUp: false,
    },
  ];

  // --- charts ---
  const outcomeArcs = Object.entries(data?.outcomes ?? {}).map(([k, n]) => ({
    label: OUTCOME_LABELS[k] ?? k,
    n,
    color: OUTCOME_COLORS[k] ?? '#64748b',
  }));
  const radar = useMemo(() => {
    const cx = 110;
    const cy = 95;
    const R = 78;
    const n = CALLSENSE_SCORE_FIELDS.length;
    const pt = (vals: number[]): string =>
      vals
        .map((v, i) => {
          const ang = -Math.PI / 2 + (i * 2 * Math.PI) / n;
          const r = (R * v) / 5;
          return `${(cx + r * Math.cos(ang)).toFixed(1)},${(cy + r * Math.sin(ang)).toFixed(1)}`;
        })
        .join(' ');
    const vals = CALLSENSE_SCORE_FIELDS.map((f) => {
      const v = data?.dimensionAverages[f];
      return typeof v === 'number' ? v : 0;
    });
    const axes = CALLSENSE_SCORE_FIELDS.map((f, i) => {
      const ang = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      return {
        label: (SCORE_LABELS[f] ?? '').split(' ')[0],
        lx: cx + 92 * Math.cos(ang),
        ly: cy + 92 * Math.sin(ang) + 3,
        x: cx + R * Math.cos(ang),
        y: cy + R * Math.sin(ang),
      };
    });
    const have = vals.filter((v) => v > 0);
    return {
      rings: [1, 3, 5].map((v) => pt(Array(n).fill(v))),
      poly: pt(vals),
      axes,
      avg: have.length ? have.reduce((a, b) => a + b, 0) / have.length : null,
    };
  }, [data?.dimensionAverages]);
  const leaders = useMemo(() => {
    const rows = [...(data?.agents ?? [])].filter((a) => a.avgOverallScore != null);
    rows.sort((a, b) => (b.avgOverallScore ?? 0) - (a.avgOverallScore ?? 0));
    const maxCalls = Math.max(1, ...rows.map((a) => a.numCalls));
    return rows.slice(0, 6).map((a) => ({
      name: a.agentName,
      score: ((a.avgOverallScore ?? 0) / 20).toFixed(2),
      pct: ((a.avgOverallScore ?? 0) / 100) * 100,
      volPct: (a.numCalls / maxCalls) * 100,
      calls: a.numCalls,
      color:
        (a.avgOverallScore ?? 0) >= 80
          ? 'var(--cr-ok)'
          : (a.avgOverallScore ?? 0) >= 60
            ? 'var(--cr-acc)'
            : 'var(--cr-warn)',
    }));
  }, [data?.agents]);
  const agentPct = total?.agentTalkPct ?? null;
  const maxDay = Math.max(1, ...trend.map((t) => t.calls));

  // --- call log ---
  const sentColors: Record<string, [string, string]> = {
    positive: ['var(--cr-okbg)', 'var(--cr-ok)'],
    neutral: ['var(--cr-sub2)', 'var(--cr-mut)'],
    negative: ['var(--cr-badbg)', 'var(--cr-bad)'],
  };
  const logRows = (calls.data?.items ?? []).filter((c) => {
    if (channel === 'cd' && c.channel !== 'CALLERDESK') return false;
    if (channel === 'wa' && c.channel !== 'WHATSAPP') return false;
    if (drillAgent && c.agentName !== drillAgent) return false;
    if (query && !(c.agentName ?? '').toLowerCase().includes(query)) return false;
    if (logF.sentiment && c.sentimentCustomer !== logF.sentiment) return false;
    if (logF.dir && c.direction !== logF.dir) return false;
    if (logF.outcome && c.callOutcome !== logF.outcome) return false;
    if (logF.low === 'y' && !(c.overallScore != null && c.overallScore < 60)) return false;
    return true;
  });
  const toggleF = (k: string, v: string): void =>
    setLogF((f) => ({ ...f, [k]: f[k] === v ? '' : v }));
  const logFilters: { k: string; v: string; label: string }[] = [
    { k: 'dir', v: 'inbound', label: 'Inbound' },
    { k: 'dir', v: 'outbound', label: 'Outbound' },
    { k: 'outcome', v: 'booked', label: 'Booked' },
    { k: 'sentiment', v: 'negative', label: 'Negative' },
    { k: 'low', v: 'y', label: 'Score < 60' },
  ];

  return (
    <div
      className="cr-scope"
      style={{ background: 'var(--cr-bg)', color: 'var(--cr-tx)', minHeight: '100%' }}
    >
      <style>{CR_TOKENS_CSS}</style>
      <div
        style={{
          maxWidth: 1500,
          margin: '0 auto',
          padding: '20px 20px 60px',
          display: 'flex',
          flexDirection: 'column',
          gap: 22,
          fontFamily: 'inherit',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 14,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '.12em',
                textTransform: 'uppercase',
                color: 'var(--cr-mut)',
              }}
            >
              Call Intelligence
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: 28,
                  fontWeight: 800,
                  letterSpacing: '-.03em',
                  lineHeight: 1,
                }}
              >
                CallSense
              </h1>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'var(--cr-ok)',
                  background: 'var(--cr-okbg)',
                  borderRadius: 999,
                  padding: '4px 10px',
                }}
              >
                <span
                  style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--cr-ok)' }}
                />
                {connected} calls analyzed
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 15, color: 'var(--cr-mut)' }}>
              CallerDesk + WhatsApp calls, AI-scored. Daily user-wise calling report, insights and
              the unified call log.
            </p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <Seg
              value={channel}
              onChange={setChannel}
              opts={[
                { v: 'all', label: 'All' },
                { v: 'cd', label: 'CallerDesk' },
                { v: 'wa', label: 'WhatsApp', soon: true },
              ]}
            />
            <Seg
              value={range}
              onChange={setRange}
              opts={[
                { v: 'today', label: 'Today' },
                { v: '7d', label: '7d' },
                { v: '30d', label: '30d' },
                { v: '90d', label: '90d' },
                { v: 'month', label: 'This month' },
              ]}
            />
            <select
              value={view}
              onChange={(e) => setView(e.target.value as typeof view)}
              style={{
                height: 32,
                padding: '0 10px',
                border: '1px solid var(--cr-bd)',
                borderRadius: 10,
                background: 'var(--cr-card)',
                color: 'var(--cr-tx)',
                font: 'inherit',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              <option value="admin">Admin view - all teams</option>
              <option value="tl">TL view - my team</option>
              <option value="agent">Agent view - one agent</option>
            </select>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Find agent"
              style={{
                height: 32,
                width: 130,
                padding: '0 10px',
                border: '1px solid var(--cr-bd)',
                borderRadius: 10,
                background: 'var(--cr-card)',
                color: 'var(--cr-tx)',
                font: 'inherit',
                fontSize: 14,
              }}
            />
          </div>
        </div>

        {/* KPI strip */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SectionHead
            label={`Overview · ${RANGE_LABEL[range]}`}
            right={
              <div style={{ fontSize: 13, color: 'var(--cr-mut)', whiteSpace: 'nowrap' }}>
                CD = CallerDesk, WA = WhatsApp
              </div>
            }
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8,minmax(0,1fr))', gap: 10 }}>
            {kpis.map((k, i) => (
              <KpiCard key={i} k={k} />
            ))}
          </div>
        </section>

        {/* Calling report */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SectionHead
            label="Calling report"
            right={
              <Seg
                value={mode}
                onChange={setMode}
                opts={[
                  { v: 'today', label: 'Today' },
                  { v: 'total', label: 'Total (range)' },
                ]}
              />
            }
          />
          <CallingReportTable
            rows={reportRows}
            loading={callingReport.isLoading}
            onPickAgent={(name) => {
              setDrillAgent((cur) => (cur === name ? null : name));
              document.getElementById('cs-call-log')?.scrollIntoView({ behavior: 'smooth' });
            }}
            activeAgent={drillAgent}
          />
        </section>

        {/* Insights */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SectionHead label={`Insights · ${RANGE_LABEL[range]}`} />
          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(12,minmax(0,1fr))', gap: 10 }}
          >
            {/* Calls over time */}
            <div
              style={{
                ...Card,
                gridColumn: 'span 8',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '14px 16px',
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8,
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 800 }}>Calls over time</div>
                <div style={{ fontSize: 13, color: 'var(--cr-mut)' }}>
                  <span style={{ color: 'var(--cr-acc)' }}>CallerDesk</span> ·{' '}
                  <span style={{ color: 'var(--cr-warn)' }}>WhatsApp soon</span> · by day
                </div>
              </div>
              {trend.length > 0 ? (
                <div style={{ display: 'flex', height: 150, alignItems: 'flex-end', gap: 2 }}>
                  {trend.map((t) => (
                    <div
                      key={t.date}
                      title={`${t.date}: ${t.calls} calls`}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        borderRadius: '3px 3px 0 0',
                        background: 'var(--cr-acc)',
                        opacity: 0.85,
                        height: `${Math.max(2, (t.calls / maxDay) * 100)}%`,
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    height: 150,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--cr-mut)',
                    fontSize: 15,
                  }}
                >
                  No calls in this range.
                </div>
              )}
            </div>

            {/* Talk-time ratio */}
            <div
              style={{
                ...Card,
                gridColumn: 'span 4',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '14px 16px',
                minWidth: 0,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 800 }}>Talk-time ratio</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span
                  style={{
                    fontSize: 32,
                    fontWeight: 800,
                    letterSpacing: '-.03em',
                    lineHeight: 1,
                    color: 'var(--cr-acc)',
                  }}
                >
                  {agentPct ?? '-'}%
                </span>
                <span style={{ fontSize: 14, color: 'var(--cr-mut)' }}>
                  agent · customer {agentPct != null ? 100 - agentPct : '-'}%
                </span>
              </div>
              <div
                style={{
                  position: 'relative',
                  height: 16,
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: 'var(--cr-sub2)',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: '40%',
                    width: '20%',
                    top: 0,
                    bottom: 0,
                    background: 'var(--cr-okbg)',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${agentPct ?? 0}%`,
                    background: 'var(--cr-acc)',
                    borderRadius: '8px 0 0 8px',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: '40%',
                    top: 0,
                    bottom: 0,
                    width: 1,
                    background: 'var(--cr-ok)',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: '60%',
                    top: 0,
                    bottom: 0,
                    width: 1,
                    background: 'var(--cr-ok)',
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 12.5,
                  color: 'var(--cr-mut)',
                }}
              >
                <span>Agent dominates &gt;70%</span>
                <span style={{ color: 'var(--cr-ok)', fontWeight: 700 }}>ideal 40-60%</span>
                <span>Passive &lt;30%</span>
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  color: 'var(--cr-mut)',
                  borderTop: '1px solid var(--cr-bd2)',
                  paddingTop: 8,
                }}
              >
                Overtalk / silence per call come online with the WhatsApp AI pipeline.
              </div>
            </div>

            <OutcomeCard arcs={outcomeArcs} connected={connected} />
            <SentimentCard
              positive={data?.sentiments?.positive ?? 0}
              neutral={data?.sentiments?.neutral ?? 0}
              negative={data?.sentiments?.negative ?? 0}
            />

            {/* Skill radar */}
            <div
              style={{
                ...Card,
                gridColumn: 'span 3',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                padding: '14px 16px',
                minWidth: 0,
              }}
            >
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <div style={{ fontSize: 15, fontWeight: 800 }}>Skill radar</div>
                <span style={{ fontSize: 13, color: 'var(--cr-mut)' }}>
                  avg{' '}
                  <b style={{ color: 'var(--cr-tx)' }}>
                    {radar.avg != null ? radar.avg.toFixed(1) : '-'}
                  </b>{' '}
                  / 5
                </span>
              </div>
              <svg
                viewBox="0 0 220 200"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              >
                {radar.rings.map((p, i) => (
                  <polygon key={i} points={p} fill="none" stroke="var(--cr-bd)" />
                ))}
                {radar.axes.map((ax, i) => (
                  <g key={i}>
                    <line x1={110} y1={95} x2={ax.x} y2={ax.y} stroke="var(--cr-bd2)" />
                    <text x={ax.lx} y={ax.ly} fontSize={8} textAnchor="middle" fill="var(--cr-mut)">
                      {ax.label}
                    </text>
                  </g>
                ))}
                <polygon
                  points={radar.poly}
                  fill="var(--cr-acc)"
                  fillOpacity={0.25}
                  stroke="var(--cr-acc)"
                  strokeWidth={1.5}
                />
              </svg>
            </div>

            {/* Leaderboard */}
            <div
              style={{
                ...Card,
                gridColumn: 'span 3',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '14px 16px',
                minWidth: 0,
              }}
            >
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <div style={{ fontSize: 15, fontWeight: 800 }}>Agent leaderboard</div>
                <span style={{ fontSize: 13, color: 'var(--cr-mut)' }}>avg score · ◆ volume</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {leaders.map((l) => (
                  <div
                    key={l.name}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '110px minmax(0,1fr) 32px',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                    }}
                  >
                    <span
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: 600,
                      }}
                    >
                      {l.name}
                    </span>
                    <div
                      style={{
                        position: 'relative',
                        height: 10,
                        borderRadius: 5,
                        background: 'var(--cr-sub2)',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${l.pct}%`,
                          background: l.color,
                          borderRadius: 5,
                        }}
                      />
                      <span
                        title={`${l.calls} calls`}
                        style={{
                          position: 'absolute',
                          top: -3,
                          left: `${l.volPct}%`,
                          fontSize: 12,
                          color: 'var(--cr-mut)',
                        }}
                      >
                        ◆
                      </span>
                    </div>
                    <b style={{ textAlign: 'right' }}>{l.score}</b>
                  </div>
                ))}
                {!leaders.length && (
                  <div
                    style={{
                      color: 'var(--cr-mut)',
                      fontSize: 13,
                      textAlign: 'center',
                      padding: '16px 0',
                    }}
                  >
                    No scored agents.
                  </div>
                )}
              </div>
            </div>

            {/* Outbound vs inbound */}
            <div
              style={{
                ...Card,
                gridColumn: 'span 6',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '14px 16px',
                minWidth: 0,
              }}
            >
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <div style={{ fontSize: 15, fontWeight: 800 }}>Outbound vs inbound</div>
                <span style={{ fontSize: 13, color: 'var(--cr-mut)' }}>
                  accepted · missed · declined
                </span>
              </div>
              {(total
                ? [
                    { label: 'Outbound', b: total.outbound },
                    { label: 'Inbound', b: total.inbound },
                  ]
                : []
              ).map((d) => {
                const t = d.b.total || 1;
                return (
                  <div key={d.label} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                      <b>
                        {d.label}{' '}
                        <span style={{ color: 'var(--cr-mut)', fontWeight: 600 }}>{d.b.total}</span>
                      </b>
                      <span style={{ fontSize: 13, color: 'var(--cr-mut)' }}>
                        accepted <b style={{ color: 'var(--cr-ok)' }}>{d.b.accepted}</b> · missed{' '}
                        <b style={{ color: 'var(--cr-warn)' }}>{d.b.missed}</b> · declined{' '}
                        <b style={{ color: 'var(--cr-bad)' }}>{d.b.declined}</b>
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        height: 14,
                        borderRadius: 7,
                        overflow: 'hidden',
                        gap: 2,
                      }}
                    >
                      <div
                        style={{
                          width: `${(d.b.accepted / t) * 100}%`,
                          background: 'var(--cr-ok)',
                        }}
                      />
                      <div
                        style={{
                          width: `${(d.b.missed / t) * 100}%`,
                          background: 'var(--cr-warn)',
                        }}
                      />
                      <div style={{ flex: 1, background: 'var(--cr-bad)' }} />
                    </div>
                  </div>
                );
              })}
              <div
                style={{
                  fontSize: 12.5,
                  color: 'var(--cr-mut)',
                  borderTop: '1px solid var(--cr-bd2)',
                  paddingTop: 8,
                }}
              >
                CallerDesk missed / declined arrive with the premium webhook. WhatsApp breakdown is
                live today.
              </div>
            </div>

            {/* Heatmap - coming online */}
            <div
              style={{
                ...Card,
                gridColumn: 'span 6',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '14px 16px',
                minWidth: 0,
                borderStyle: 'dashed',
                borderColor: 'var(--cr-warn)',
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 800 }}>
                When calls happen{' '}
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--cr-warn)' }}>
                  coming online
                </span>
              </div>
              <div style={{ fontSize: 13.5, color: 'var(--cr-mut)' }}>
                The weekday x hour heatmap needs per-hour call timestamps - lands with the
                CallerDesk webhook capture.
              </div>
            </div>
          </div>
        </section>

        {/* Call log */}
        <section id="cs-call-log" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SectionHead
            label="Call log"
            right={
              <span style={{ fontSize: 13, color: 'var(--cr-mut)' }}>
                {drillAgent ? (
                  <button
                    type="button"
                    onClick={() => setDrillAgent(null)}
                    style={{
                      border: 0,
                      background: 'var(--cr-accbg)',
                      color: 'var(--cr-acc)',
                      fontWeight: 700,
                      borderRadius: 999,
                      padding: '2px 10px',
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    Drilled: {drillAgent} ✕
                  </button>
                ) : (
                  `${logRows.length} calls · ${RANGE_LABEL[range]}`
                )}
              </span>
            }
          />
          <div style={{ ...Card, overflow: 'hidden' }}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                padding: '10px 14px',
                borderBottom: '1px solid var(--cr-bd2)',
                alignItems: 'center',
              }}
            >
              {logFilters.map((f) => {
                const on = logF[f.k] === f.v;
                return (
                  <button
                    key={f.label}
                    type="button"
                    onClick={() => toggleF(f.k, f.v)}
                    style={{
                      height: 30,
                      padding: '0 10px',
                      border: `1px solid ${on ? 'var(--cr-acc)' : 'var(--cr-bd)'}`,
                      borderRadius: 8,
                      background: on ? 'var(--cr-accbg)' : 'var(--cr-card)',
                      color: on ? 'var(--cr-acc)' : 'var(--cr-tx)',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {f.label}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setLogF({})}
                style={{
                  height: 30,
                  padding: '0 10px',
                  border: 0,
                  background: 'transparent',
                  color: 'var(--cr-acc)',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Clear
              </button>
            </div>
            <div style={{ overflow: 'auto', maxHeight: 520 }}>
              <table style={{ width: '100%', minWidth: 1000, fontSize: 14 }}>
                <thead>
                  <tr>
                    {[
                      'Date / time',
                      'Channel',
                      'Dir',
                      'Agent',
                      'Customer',
                      'Duration',
                      'Talk ratio',
                      'Outcome',
                      'Sentiment',
                      'Score',
                      'Rec',
                    ].map((h, i) => (
                      <th
                        key={h}
                        style={{
                          position: 'sticky',
                          top: 0,
                          background: 'var(--cr-sub)',
                          padding: '8px 10px',
                          borderBottom: '1px solid var(--cr-bd)',
                          fontSize: 13,
                          fontWeight: 700,
                          color: 'var(--cr-mut)',
                          textAlign:
                            i === 2 || i === 10 ? 'center' : i === 5 || i === 9 ? 'right' : 'left',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logRows.map((c: CallsenseCallRow) => {
                    const wa = c.channel === 'WHATSAPP';
                    const sc = sentColors[c.sentimentCustomer] ?? ['transparent', 'var(--cr-mut)'];
                    return (
                      <tr
                        key={c.id}
                        onClick={() => setOpenCallId(c.id)}
                        style={{
                          cursor: 'pointer',
                          background: openCallId === c.id ? 'var(--cr-accbg)' : 'transparent',
                        }}
                      >
                        <td
                          style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid var(--cr-bd2)',
                            whiteSpace: 'nowrap',
                            color: 'var(--cr-mut)',
                          }}
                        >
                          {formatCallDate(c.callDate)}
                        </td>
                        <td
                          style={{ padding: '8px 10px', borderBottom: '1px solid var(--cr-bd2)' }}
                        >
                          <span
                            style={{
                              fontSize: 12.5,
                              fontWeight: 700,
                              borderRadius: 6,
                              padding: '2px 7px',
                              background: wa ? 'var(--cr-okbg)' : 'var(--cr-accbg)',
                              color: wa ? 'var(--cr-wa)' : 'var(--cr-acc)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {wa ? 'WhatsApp' : 'CallerDesk'}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid var(--cr-bd2)',
                            textAlign: 'center',
                            color: 'var(--cr-mut)',
                          }}
                          title={c.direction}
                        >
                          {c.direction === 'inbound' ? '↙' : c.direction === 'outbound' ? '↗' : '·'}
                        </td>
                        <td
                          style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid var(--cr-bd2)',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.agentName ?? '-'}
                        </td>
                        <td
                          style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid var(--cr-bd2)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.callReason ?? '-'}{' '}
                          <span style={{ color: 'var(--cr-mut)', fontSize: 13 }}>
                            · {c.destination ?? '-'}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid var(--cr-bd2)',
                            textAlign: 'right',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.durationMinutes != null ? `${c.durationMinutes.toFixed(1)}m` : '-'}
                        </td>
                        <td
                          style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid var(--cr-bd2)',
                            minWidth: 90,
                          }}
                        >
                          <div
                            title={
                              c.agentTalkPct != null
                                ? `agent ${c.agentTalkPct}% · customer ${100 - c.agentTalkPct}%`
                                : 'no talk split'
                            }
                            style={{
                              display: 'flex',
                              height: 6,
                              borderRadius: 3,
                              overflow: 'hidden',
                              background: 'var(--cr-sub2)',
                              opacity: c.agentTalkPct != null ? 1 : 0.2,
                            }}
                          >
                            <div
                              style={{
                                width: `${c.agentTalkPct ?? 50}%`,
                                background: 'var(--cr-acc)',
                              }}
                            />
                          </div>
                        </td>
                        <td
                          style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid var(--cr-bd2)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {OUTCOME_LABELS[c.callOutcome] ?? c.callOutcome}
                        </td>
                        <td
                          style={{ padding: '8px 10px', borderBottom: '1px solid var(--cr-bd2)' }}
                        >
                          <span
                            style={{
                              fontSize: 12.5,
                              fontWeight: 700,
                              borderRadius: 6,
                              padding: '2px 7px',
                              background: sc[0],
                              color: sc[1],
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {c.sentimentCustomer}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid var(--cr-bd2)',
                            textAlign: 'right',
                            fontWeight: 800,
                            color:
                              c.overallScore == null
                                ? 'var(--cr-mut)'
                                : c.overallScore >= 80
                                  ? 'var(--cr-ok)'
                                  : c.overallScore >= 60
                                    ? 'var(--cr-tx)'
                                    : 'var(--cr-bad)',
                          }}
                        >
                          {c.overallScore != null ? c.overallScore.toFixed(0) : '-'}
                        </td>
                        <td
                          style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid var(--cr-bd2)',
                            textAlign: 'center',
                          }}
                        >
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              background: 'var(--cr-sub2)',
                              color: 'var(--cr-tx)',
                              fontSize: 11,
                              opacity: c.recordingUrl ? 1 : 0.25,
                            }}
                          >
                            ▶
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {!calls.isLoading && !logRows.length && (
                    <tr>
                      <td
                        colSpan={11}
                        style={{ padding: 24, textAlign: 'center', color: 'var(--cr-mut)' }}
                      >
                        No calls match these filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      <CallDetailDrawer callId={openCallId} onClose={() => setOpenCallId(null)} />
    </div>
  );
};
